import type { LandingHeroCopy, LandingHeroImageAdmin, ProductSummary } from '@repo/api-contracts';
import type { ChangeEvent, ReactElement } from 'react';
import { Button } from '@/shared/ui';
import { LandingHeroCopyForm } from './landing-hero-copy-form';

/** 선택 상자의 "연결 안 함" 값 — 상품 id(uuid)와 겹치지 않는다. */
const NO_PRODUCT = '';

interface LandingHeroImageCardProps {
  readonly image: LandingHeroImageAdmin;
  /** 연결할 수 있는 공개 상품 — 연결 상품 선택 상자의 항목. */
  readonly productOptions: readonly Pick<ProductSummary, 'id' | 'name'>[];
  /** 1부터 시작하는 노출 순번. */
  readonly order: number;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly isBusy: boolean;
  readonly onMove: (direction: 'UP' | 'DOWN') => void;
  readonly onReplace: (file: File) => void;
  readonly onDelete: () => void;
  /** 연결 상품을 바꾼다 — null 이면 연결을 끊는다. */
  readonly onChangeProduct: (productId: string | null) => void;
  /** 배너 문구를 통째로 저장한다 — 모든 필드가 null 이면 문구를 지운다. */
  readonly onSaveCopy: (copy: LandingHeroCopy) => void;
}

const ACCEPT = 'image/jpeg,image/png,image/webp';

/** 히어로 배너 한 장 — 미리보기, 순번, 연결 상품, 배너 문구, 위·아래, 교체, 삭제. */
export const LandingHeroImageCard = ({
  image,
  productOptions,
  order,
  isFirst,
  isLast,
  isBusy,
  onMove,
  onReplace,
  onDelete,
  onChangeProduct,
  onSaveCopy,
}: LandingHeroImageCardProps): ReactElement => {
  const linked = image.product;
  const isLinkedPublic = linked !== null && linked.status === 'PUBLISHED' && !linked.isDeleted;
  // 연결 뒤 숨김·삭제된 상품은 공개 목록에 없다 — 선택 상자가 현재 값을 잃지 않게 항목을 덧붙인다.
  const isLinkedListed =
    linked !== null && productOptions.some((option) => option.id === linked.id);

  const handleProductChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const productId = event.target.value === NO_PRODUCT ? null : event.target.value;

    if (productId !== (linked?.id ?? null)) {
      onChangeProduct(productId);
    }
  };

  const handleReplace = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (file !== undefined) {
      onReplace(file);
    }
    // 같은 파일을 다시 골라도 change 가 나도록 비운다.
    event.target.value = '';
  };

  return (
    <li className="flex flex-col gap-3 rounded-lg border p-3">
      <div className="relative aspect-[16/9] overflow-hidden rounded-md bg-muted">
        <img
          src={image.imageUrl}
          alt={`히어로 이미지 ${order}`}
          className="size-full object-cover"
        />
        <span className="absolute top-2 left-2 rounded-full bg-background px-2.5 py-0.5 text-xs font-semibold tabular-nums">
          {order}
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={`landing-hero-product-${image.id}`} className="text-sm font-medium">
          연결 상품
        </label>
        <select
          id={`landing-hero-product-${image.id}`}
          aria-label={`${order}번 이미지 연결 상품`}
          value={linked?.id ?? NO_PRODUCT}
          disabled={isBusy}
          onChange={handleProductChange}
          className="h-9 rounded-2xl border border-input bg-background px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value={NO_PRODUCT}>연결 안 함</option>
          {linked !== null && !isLinkedListed ? (
            <option value={linked.id}>{linked.name} (비공개)</option>
          ) : null}
          {productOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        {linked === null ? (
          <p className="text-xs text-muted-foreground">
            연결 상품이 없으면 HOME 에 아래 배너 문구만 나옵니다 — 문구도 비우면 사진만 나옵니다.
          </p>
        ) : isLinkedPublic ? (
          <p className="text-xs text-muted-foreground">
            배너 문구를 비운 칸은 <strong className="text-foreground">{linked.name}</strong> 의
            이름·가격·보기 버튼으로 채워집니다.
          </p>
        ) : (
          <p role="status" className="text-xs text-destructive">
            {linked.name} 은(는) 지금 공개 상품이 아니라 HOME 에 상품 정보가 나오지 않습니다. 다른
            상품을 골라 주세요.
          </p>
        )}
      </div>

      {/* 저장 뒤 목록이 다시 오면 updatedAt 이 바뀌어 폼이 서버 값으로 새로 시작한다. */}
      <LandingHeroCopyForm
        key={image.updatedAt}
        image={image}
        order={order}
        isBusy={isBusy}
        onSave={onSaveCopy}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={isFirst || isBusy}
          onClick={() => onMove('UP')}
          aria-label={`${order}번 이미지 위로`}
        >
          위로
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isLast || isBusy}
          onClick={() => onMove('DOWN')}
          aria-label={`${order}번 이미지 아래로`}
        >
          아래로
        </Button>
        <label className="ml-auto inline-flex h-8 cursor-pointer items-center rounded-full border border-input px-3 text-sm font-medium hover:bg-accent has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring">
          교체
          <input
            type="file"
            accept={ACCEPT}
            disabled={isBusy}
            onChange={handleReplace}
            aria-label={`${order}번 이미지 교체`}
            className="sr-only"
          />
        </label>
        <Button
          size="sm"
          variant="ghost"
          disabled={isBusy}
          onClick={onDelete}
          aria-label={`${order}번 이미지 삭제`}
        >
          삭제
        </Button>
      </div>
    </li>
  );
};
